<div class="matches-list-shell" [class.rtl]="languageService.currentLanguage() === 'ar'">

	<!-- Dashboard-style Header with Integrated Stats -->
	<header class="flex flex-column md:flex-row md:align-items-center justify-content-between gap-4">
		<div>
			<h2 class="text-2xl font-bold text-900 m-0 tracking-tight">{{ 'MATCHES.CANDIDATES' | translate }}</h2>
			<p class="text-500 mt-1 mb-0">{{ 'MATCHES.DESC' | translate }}</p>
		</div>

		<!-- Quick Stats Grid -->
		<div class="flex gap-3 overflow-x-auto pb-1 md:pb-0">
			@for (summary of summaryItems(); track summary.label) {
			<div
				class="flex-shrink-0 flex flex-column justify-content-center px-4 py-2 border-1 border-gray-300 border-round-lg bg-white">
				<span class="text-xs text-500 font-medium uppercase tracking-wider">{{ summary.label }}</span>
				<span class="text-xl font-bold text-900 mt-1">{{ summary.count }}</span>
			</div>
			}
		</div>
	</header>

	<!-- Main Content Area: Tabs & List -->
	<div class="flex flex-column gap-3">

		@if (loading()) {
		<!-- Dashboard Loading Skeleton -->
		<div class="flex flex-column gap-4">
			<!-- Skeleton Tabs -->
			<div class="flex gap-2 border-bottom-1 border-gray-300 pb-1">
				<p-skeleton width="8rem" height="3rem" borderRadius="0"></p-skeleton>
				<p-skeleton width="6rem" height="3rem" borderRadius="0"></p-skeleton>
				<p-skeleton width="7rem" height="3rem" borderRadius="0"></p-skeleton>
				<p-skeleton width="5rem" height="3rem" borderRadius="0"></p-skeleton>
			</div>

			<!-- Skeleton List Container -->
			<div class="border-1 border-gray-300 border-round-2xl overflow-hidden bg-white">
				<!-- Skeleton Table Header -->
				<div class="hidden md:flex align-items-center px-4 py-3 bg-surface-50 border-bottom-1 border-gray-300">
					<div class="w-4"><p-skeleton width="40%" height="1rem"></p-skeleton></div>
					<div class="w-3"><p-skeleton width="50%" height="1rem"></p-skeleton></div>
					<div class="w-3 text-right flex justify-content-end"><p-skeleton width="40%"
							height="1rem"></p-skeleton></div>
					<div class="w-2 text-right flex justify-content-end"><p-skeleton width="30%"
							height="1rem"></p-skeleton></div>
				</div>

				<!-- Skeleton Rows -->
				@for (i of [1, 2, 3, 4, 5]; track i; let last = $last) {
				<div class="flex flex-column md:flex-row md:align-items-center gap-4 px-4 py-4"
					[class.border-bottom-1]="!last" [class.border-gray-300]="!last">

					<div class="flex-1 md:w-4 flex align-items-center gap-3">
						<p-skeleton shape="circle" size="3.5rem" class="flex-shrink-0"></p-skeleton>
						<div class="flex flex-column gap-2 w-full">
							<p-skeleton width="70%" height="1.5rem"></p-skeleton>
							<p-skeleton width="40%" height="1rem"></p-skeleton>
						</div>
					</div>

					<div class="flex-1 md:w-3 flex flex-column gap-2">
						<p-skeleton width="90%" height="1.2rem"></p-skeleton>
						<p-skeleton width="60%" height="1.2rem"></p-skeleton>
					</div>

					<div
						class="flex-1 md:w-3 flex flex-row md:flex-column justify-content-between md:justify-content-center md:align-items-end gap-2">
						<p-skeleton width="50%" height="1.5rem"></p-skeleton>
						<p-skeleton width="70%" height="1rem"></p-skeleton>
					</div>

					<div class="flex-1 md:w-2 flex justify-content-end">
						<p-skeleton width="100%" height="2.5rem" borderRadius="8px"></p-skeleton>
					</div>
				</div>
				}
			</div>
		</div>
		} @else {
		<!-- Clean Tab Navigation -->
		<p-tabs [value]="activeKey || initialKey()" class="w-full">
			<p-tablist class="border-bottom-1 border-gray-300 mb-4">
				@for (group of matchGroups(); track group.key; let i = $index) {
				<p-tab [value]="group.key"
					class="pb-3 border-bottom-2 border-transparent transition-colors transition-duration-200 hover:border-300 font-medium text-600 px-3 cursor-pointer select-none gap-2">
					<span>{{ group.label }}</span>
					<span class="bg-surface-200 text-600 px-2 py-0 border-round text-xs font-bold">{{
						group.matches.length }}</span>
				</p-tab>
				}
			</p-tablist>

			<p-tabpanels class="p-0">
				@for (group of matchGroups(); track group.key) {
				<p-tabpanel [value]="group.key" class="p-0">

					<!-- Wrapper for Bordered List -->
					<div class="border-1 border-gray-300 border-round-2xl overflow-hidden bg-white">

						<!-- List Header (Pseudo-table header) -->
						<div
							class="hidden md:flex align-items-center px-4 py-3 bg-surface-50 border-bottom-1 border-gray-300 text-xs font-semibold text-500 uppercase tracking-wider">
							<div class="w-4">{{ 'MATCHES.CARRIER_DETAILS' | translate }}</div>
							<div class="w-3">{{ 'MATCHES.EQUIPMENT_ATTRIBUTES' | translate }}</div>
							<div class="w-3 text-right">{{ 'MATCHES.RATE_DISTANCE' | translate }}</div>
							<div class="w-2 text-right">{{ 'MATCHES.ACTIONS' | translate }}</div>
						</div>

						<!-- Empty State -->
						@if (group.matches.length === 0) {
						<div
							class="flex flex-column align-items-center justify-content-center py-8 text-center text-500">
							<fa-icon [icon]="icons.inbox" class="text-4xl mb-3 opacity-50"></fa-icon>
							<p>{{ 'MATCHES.NO_MATCHES' | translate }}</p>
						</div>
						}

						<!-- Match Rows -->
						@for (match of group.matches; track match.id; let last = $last) {
						<div class="group relative flex flex-column md:flex-row md:align-items-center gap-4 px-4 py-4 hover:bg-surface-50 transition-colors transition-duration-150 cursor-pointer"
							[class.border-bottom-1]="!last" [class.border-gray-300]="!last">

							<!-- 1. Carrier Profile (w-4) -->
							<div class="flex-1 md:w-4 flex align-items-center gap-3 min-w-0">
								<div class="relative flex-shrink-0">
									@if (match.avatar) {
									<p-avatar [image]="match.avatar.image"
										[label]="!match.avatar.image ? match.avatar.label : undefined" shape="circle"
										size="large"
										[class]="'bg-surface-200 text-700 font-bold ' + (match.preferred ? 'border-2 border-green-500' : '')">
									</p-avatar>
									}
									@if (match.preferred) {
									<div
										class="absolute -bottom-1 -right-1 bg-green-500 text-white w-1rem h-1rem border-circle flex align-items-center justify-content-center border-2 border-white">
										<fa-icon [icon]="icons.star" style="font-size: 0.5rem"></fa-icon>
									</div>
									}
								</div>

								<div class="flex flex-column gap-1 min-w-0">
									<div class="flex align-items-center gap-2">
										<span
											class="text-900 font-semibold text-lg white-space-nowrap overflow-hidden text-overflow-ellipsis">{{
											match.matchNumber }}</span>
										<p-tag [value]="match.status" [severity]="getStatusSeverity(match.status)"
											class="text-xs"></p-tag>
									</div>
									<div class="flex flex-wrap align-items-center gap-2 text-sm text-500">
										<span class="font-mono bg-surface-100 px-1 border-round text-600">LR#{{
											match.loadRequestNumber }}</span>
										<span class="font-mono bg-surface-100 px-1 border-round text-600">LA#{{
											match.loadAvailabilityNumber }}</span>
									</div>
								</div>
							</div>

							<!-- 2. Equipment & Badges (w-3) -->
							<div class="flex-1 md:w-3 flex flex-column gap-2">
								<div class="flex flex-wrap gap-1">
									@for (eq of match.equipment; track eq) {
									<span
										class="px-2 py-1 bg-blue-50 text-blue-700 border-round text-xs font-semibold inline-flex align-items-center gap-1"><fa-icon [icon]="icons.truck"></fa-icon>{{
										eq }}</span>
									}
								</div>
								<div class="flex flex-wrap gap-1">
									@for (badge of match.badges; track badge) {
									<span class="px-2 py-0 border-1 border-gray-300 text-500 border-round text-xs inline-flex align-items-center gap-1"><fa-icon [icon]="icons.info"></fa-icon>{{
										badge
										}}</span>
									}
								</div>
							</div>

							<!-- 3. Rate & Logistics (w-3) -->
							<div
								class="flex-1 md:w-3 flex flex-row md:flex-column justify-content-between md:justify-content-center md:align-items-end gap-1">
								<div class="flex align-items-baseline gap-2">
									<span class="text-xl font-bold text-900">{{ match.offerAmount }}</span>
								</div>
								<div class="flex align-items-center gap-3 text-sm text-500">
									<span>{{ match.ratePerMile }}</span>
									<span>&bull;</span>
									<span>{{ match.distanceAway }} {{ 'MATCHES.AWAY' | translate }}</span>
								</div>
								<div class="text-xs text-primary font-medium mt-1">{{ 'MATCHES.ETA' | translate }}: {{
									match.eta }}</div>
							</div>

							<!-- 4. Actions (w-2) -->
							<div
								class="flex-1 md:w-2 flex flex-row md:flex-column gap-2 justify-content-end md:align-items-end">
								@for (action of match.actions; track action.label) {
								<button pButton [label]="action.label"
									[class]="'w-full md:w-auto ' + (action.styleClass || '') + ' p-button-sm font-bold border-round-lg icon-button'"
									[class.p-button-outlined]="action.label !== 'Book Now' && action.label !== 'Accept'"
									(click)="$event.stopPropagation(); actionClick.emit({match, action})">
									<fa-icon [icon]="action.label === 'View Details' ? icons.external : icons.bolt"></fa-icon>
								</button>
								}
							</div>

						</div>
						}
					</div>

				</p-tabpanel>
				}
			</p-tabpanels>
		</p-tabs>
		}

	</div>
</div>
